'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { syncWithAnimationFrame } from 'src/utils/dom-utils/utils';
import {
  HEADER_STATE_CHANGE,
  SEARCH_PANEL_CLOSE_FLYOUT,
  SEARCH_PANEL_OPEN_FLYOUT,
  subscribe,
  unsubscribe,
} from 'src/utils/events';
import { useEventListener, useResizeObserver } from 'usehooks-ts';
import HTMLRenderer from '../html-renderer/html-renderer.component';
import styles from './search-panel.component.module.less';

// --- Hooks ------------------------------------------------------------------

/**
 * Event listener for header
 */
function useHeaderState() {
  const [headerVisible, setHeaderVisible] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(80);

  useEffect(() => {
    function onHeaderStateChange(
      e: CustomEvent<{
        visible: boolean;
        height: number;
      }>,
    ) {
      setHeaderVisible(e.detail.visible);
      setHeaderHeight(e.detail.height);
    }

    subscribe(HEADER_STATE_CHANGE, onHeaderStateChange);
    return () => {
      unsubscribe(HEADER_STATE_CHANGE, onHeaderStateChange);
    };
  }, []);

  return { headerVisible, headerHeight } as const;
}

/**
 * Event listener for search panel
 */
function useSearchExperienceState() {
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  useEffect(() => {
    const onFlyoutOpen = (e: Event) => {
      setFlyoutOpen(true);
    };

    const onFlyoutClose = (e: Event) => {
      setFlyoutOpen(false);
    };

    subscribe(SEARCH_PANEL_OPEN_FLYOUT, onFlyoutOpen);
    subscribe(SEARCH_PANEL_CLOSE_FLYOUT, onFlyoutClose);

    return () => {
      unsubscribe(SEARCH_PANEL_OPEN_FLYOUT, onFlyoutOpen);
      unsubscribe(SEARCH_PANEL_CLOSE_FLYOUT, onFlyoutClose);
    };
  }, []);

  return { flyoutOpen } as const;
}

const scrollUpThreshold = 600; // the amount of pixels the user needs to scroll up before the panel is shown again
const initialDisplayState = { pinned: false, hidden: false, transition: false };

interface SearchPanelComponentProps {
  componentId?: string;
  ESIString: string;
  displayButton: boolean;
  dataCy?: string;
  isVisible?: boolean;
  autoHide?: boolean;
}

const SearchPanelComponent = ({
  componentId,
  ESIString,
  dataCy = 'search-panel',
  isVisible = true,
  autoHide = false,
}: SearchPanelComponentProps) => {
  const { headerVisible, headerHeight } = useHeaderState();
  const { flyoutOpen } = useSearchExperienceState();
  const [{ hidden, pinned, transition }, setDisplayState] =
    useState(initialDisplayState);
  const elementRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const lastScrollYRef = useRef(0);
  const scrollUpStartRef = useRef<number | null>(null);
  const scrollDownStartRef = useRef<number | null>(null);

  const { height = 0 } = useResizeObserver({
    ref: elementRef,
  });

  // set the initial scroll position on render
  useEffect(() => {
    lastScrollYRef.current = window.scrollY;
  }, []);

  // where the magic happens
  const calculateState = useCallback(() => {
    const spacer = spacerRef.current;
    const element = elementRef.current;

    // if the search panel has flyoutOpen (the mobile view) we don't want any of this behaviour
    if (flyoutOpen || !spacer || !element) {
      setDisplayState(initialDisplayState);
      return;
    }

    // set up the variables to calcuate the scroll direction
    const currentScrollY = window.scrollY;
    const previousScrollY = lastScrollYRef.current;
    const scrollingDown = currentScrollY > previousScrollY;
    const scrollingUp = currentScrollY < previousScrollY;

    // get the bounding rectangle of the spacer element
    const {
      top: spacerTop,
      bottom: spacerBottom,
      height: spacerHeight,
    } = spacer.getBoundingClientRect();
    // the threshold for pinning the panel - the top of the spacer, adjusted for the header height
    let pinnedThreshold = spacerTop - headerHeight;
    let shouldHide = null;

    if (scrollingDown) {
      // if we weren't already scrolling down
      if (scrollDownStartRef.current === null) {
        // set the start point for scrolling down
        scrollDownStartRef.current = previousScrollY;
        // reset the position for scrolling up
        scrollUpStartRef.current = null;
      }
      // workout how much we've scrolled down
      const scrolledDown = (scrollDownStartRef.current - currentScrollY) * -1;
      // hide if we've scrolled down more than the height of the panel and are at least one panel height away from it
      shouldHide = spacerBottom + spacerHeight < 0;
    }

    if (scrollingUp) {
      // if we weren't already scrolling up
      if (scrollUpStartRef.current === null) {
        // set the start point for scrolling up
        scrollUpStartRef.current = previousScrollY;
        // reset the position for scrolling down
        scrollDownStartRef.current = null;
      }
      // workout how much we've scrolled up
      const scrolledUp = scrollUpStartRef.current - currentScrollY;

      // Calculate document height for threshold
      const documentOffsetHeight =
        document.documentElement?.offsetHeight ||
        document.body?.offsetHeight ||
        0;
      // Threshold is 1/4 of the page or the pixel value defined, whichever is smaller
      let threshold = Math.min(documentOffsetHeight / 4, scrollUpThreshold);

      // show if we've scrolled up enough or we are approaching the panel (it's own height away from the bottom)
      if (scrolledUp >= threshold || spacerBottom + spacerHeight >= 0) {
        scrollUpStartRef.current = null;
        shouldHide = false;
      }
    }

    // update the last scroll position
    lastScrollYRef.current = currentScrollY;

    setDisplayState(prev => {
      const shouldPin = pinnedThreshold < 0;

      return {
        pinned: shouldPin,
        hidden: shouldHide ?? prev.hidden,
        transition: prev.pinned === shouldPin, // turn off transition when changing pinning so it snaps in / out of pinned mode (stops the bounce effect when you reach the top when scrolling quickly as it's transitioning the showing/hiding at the same time)
      };
    });
  }, [hidden, flyoutOpen, height, headerHeight, headerVisible]);

  const onScroll = syncWithAnimationFrame(() => {
    if (!autoHide) return;
    calculateState();
  });

  useEventListener('scroll', onScroll, undefined, { passive: true });

  // simulates an escape key press if the panel goes from visible to hidden
  // pretty gross but smallest amount of code to get the search panel to close all dropdowns when it hides
  useEffect(() => {
    if (hidden) {
      const escape = {
        key: 'Escape',
        code: 'Escape',
        keyCode: 27,
        which: 27,
        bubbles: true,
        cancelable: true,
      };
      document.dispatchEvent(new KeyboardEvent('keydown', escape));
      document.dispatchEvent(new KeyboardEvent('keyup', escape));
    }
  }, [hidden]);

  if (!isVisible || !ESIString) {
    return null;
  }

  return (
    <div
      id={componentId}
      data-cy={dataCy}
      className={styles.spacer}
      ref={spacerRef}
      style={{
        height,
      }}>
      <div
        ref={elementRef}
        className={classNames(styles.container, {
          [styles.pinned]: pinned,
          [styles.hidden]: hidden,
          [styles.headerVisible]: headerVisible,
          [styles.transition]: transition,
        })}
        style={
          {
            ['--header-height']: `${headerHeight}px`,
          } as React.CSSProperties
        }>
        <HTMLRenderer
          className={styles.inner}
          htmlString={ESIString}
          element="div"
        />
      </div>
    </div>
  );
};

export default SearchPanelComponent;
